import { useEffect } from 'react';
import { useAppSettings } from '@/contexts/AppSettingsContext';

export default function DynamicFavicon() {
  const { settings } = useAppSettings();

  useEffect(() => {
    // Prioritize SVG code over URL
    const faviconSvgCode = settings?.favicon_svg_code;
    const faviconUrl = settings?.favicon_url;
    
    // Convert SVG code to data URI if present
    const faviconDataUri = faviconSvgCode 
      ? `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(faviconSvgCode)))}`
      : null;
    const finalFaviconUrl = faviconDataUri || faviconUrl;

    if (finalFaviconUrl) {
      // Remove ALL existing favicon links (including static fallbacks)
      // This includes: rel="icon", rel="shortcut icon", rel="apple-touch-icon"
      const existingFavicons = document.querySelectorAll('link[rel*="icon"], link[rel="shortcut icon"]');
      existingFavicons.forEach(favicon => favicon.remove());

      // Add cache-busting query parameter to force browser reload (only for URLs, not data URIs)
      const faviconUrlWithCacheBust = faviconDataUri 
        ? faviconDataUri 
        : `${faviconUrl}?v=${Date.now()}`;

      // Detect MIME type
      const getMimeType = (url: string): string => {
        if (url.startsWith('data:image/svg+xml')) return 'image/svg+xml';
        const urlLower = url.toLowerCase();
        if (urlLower.endsWith('.webp')) return 'image/webp';
        if (urlLower.endsWith('.png')) return 'image/png';
        if (urlLower.endsWith('.svg')) return 'image/svg+xml';
        if (urlLower.endsWith('.ico')) return 'image/x-icon';
        if (urlLower.endsWith('.jpg') || urlLower.endsWith('.jpeg')) return 'image/jpeg';
        // Default to generic image type if extension is unknown
        return 'image/x-icon';
      };

      // Add new favicon
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconUrlWithCacheBust;
      link.type = getMimeType(faviconUrlWithCacheBust);
      // Add sizes attribute for better browser compatibility
      // For WebP/PNG favicons, use "any" to indicate any size is acceptable
      // For SVG, sizes is not needed
      if (!faviconDataUri && !faviconUrl?.toLowerCase().endsWith('.svg')) {
        link.setAttribute('sizes', '32x32');
      }
      document.head.appendChild(link);

      // Also add apple-touch-icon if it's the same image (only for URLs, not data URIs)
      if (!faviconDataUri) {
        const appleTouchIcon = document.createElement('link');
        appleTouchIcon.rel = 'apple-touch-icon';
        appleTouchIcon.href = faviconUrlWithCacheBust;
        document.head.appendChild(appleTouchIcon);
      }
    }
  }, [settings?.favicon_url, settings?.favicon_svg_code]);

  return null;
}
